import { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import intl from 'react-intl-universal';
import { useAppThunkDispatch } from 'state/store';
import {
  selectCapacityFilters,
  upsertFiltersSettings,
} from 'state/Capacities/Filters/filtersSlice';
import { AppliedFilter } from 'utils/customTypes';
import { TeamsCapacityFilters } from 'types/store/teamsCapacity';

export const useAppliedFilters = () => {
  const dispatch = useAppThunkDispatch();
  const capacityFilters = useSelector(selectCapacityFilters);

  const appliedFilters = useMemo(() => {
    const tags: AppliedFilter[] = [];
    if (capacityFilters?.status) {
      const statusTags = capacityFilters.status.map<AppliedFilter>((id) => ({
        key: 'status',
        value: id,
        label: intl.get(`PROJECT_DETAIL.STATUS_OPTIONS.${id.toUpperCase()}`),
      }));
      tags.push(...statusTags);
    }

    if (capacityFilters?.archivedProjects === true) {
      tags.push({
        key: 'archivedProjects',
        value: '',
        label: intl.get('TEAMS_CAPACITY.SIDE_PANEL_FILTERS.ARCHIVED_PROJECTS'),
      });
    }
    if (capacityFilters?.deactivatedUsers === true) {
      tags.push({
        key: 'deactivatedUsers',
        value: '',
        label: intl.get('TEAMS_CAPACITY.SIDE_PANEL_FILTERS.DEACTIVATED_USERS'),
      });
    }
    return tags;
  }, [capacityFilters]);

  const handleRemoveFilter = useCallback(
    (removedFilter: AppliedFilter) => {
      const copiedFilters = { ...capacityFilters } as TeamsCapacityFilters;

      if (removedFilter.key === 'status') {
        const updatedStatus =
          copiedFilters.status?.filter(
            (value) => value !== removedFilter.value
          ) || [];
        copiedFilters.status = updatedStatus;
      } else if (removedFilter.key === 'archivedProjects') {
        copiedFilters.archivedProjects = undefined;
      } else if (removedFilter.key === 'deactivatedUsers') {
        copiedFilters.deactivatedUsers = undefined;
      }
      dispatch(upsertFiltersSettings({ filters: copiedFilters }));
    },
    [capacityFilters, dispatch]
  );

  const handleClearFilters = useCallback(() => {
    dispatch(
      upsertFiltersSettings({
        filters: {
          status: [],
          archivedProjects: undefined,
          deactivatedUsers: undefined,
        },
      })
    );
  }, [dispatch]);

  return {
    appliedFilters,
    handleRemoveFilter,
    handleClearFilters,
  };
};
